import { FC, useCallback, useState, useMemo } from "react"
import { LOMapEntry } from "../../../../types/pricing-engine"
import { LightBtn, LinkBtnDanger } from "../../buttons"
import { Plus, Trash2, X, AlertTriangle } from "lucide-react"

type FieldType = "default" | "raw"

interface FieldRow {
  id: string
  type: FieldType
  key: string
  value: string
}

interface Props {
  entry: LOMapEntry
  onChange: (entry: LOMapEntry) => void
  onClose: () => void
}

/**
 * Converts LOMapEntry defaults and raw_defaults into a flat list of field rows
 */
const entryToRows = (entry: LOMapEntry): FieldRow[] => {
  const rows: FieldRow[] = []

  if (entry.defaults) {
    Object.entries(entry.defaults).forEach(([key, value]) => {
      rows.push({
        id: `default-${key}-${Date.now()}-${Math.random()}`,
        type: "default",
        key,
        value: String(value)
      })
    })
  }

  if (entry.raw_defaults) {
    Object.entries(entry.raw_defaults).forEach(([key, value]) => {
      rows.push({
        id: `raw-${key}-${Date.now()}-${Math.random()}`,
        type: "raw",
        key,
        value: String(value)
      })
    })
  }

  return rows
}

/**
 * Converts field rows back into LOMapEntry defaults and raw_defaults
 */
const rowsToEntry = (entry: LOMapEntry, rows: FieldRow[]): LOMapEntry => {
  const defaults: Record<string, any> = {}
  const raw_defaults: Record<string, any> = {}

  rows.forEach(row => {
    if (!row.key.trim()) return

    // Auto-detect numbers
    const value = row.value.trim()
    const numValue = Number(value)
    const finalValue =
      value !== "" && !isNaN(numValue) && isFinite(numValue) ? numValue : value

    if (row.type === "default") {
      defaults[row.key.trim()] = finalValue
    } else {
      raw_defaults[row.key.trim()] = finalValue
    }
  })

  return {
    ...entry,
    defaults: Object.keys(defaults).length > 0 ? defaults : undefined,
    raw_defaults:
      Object.keys(raw_defaults).length > 0 ? raw_defaults : undefined
  }
}

const LOEntryEditor: FC<Props> = ({ entry, onChange, onClose }) => {
  const [rows, setRows] = useState<FieldRow[]>(() => entryToRows(entry))
  const [newFieldType, setNewFieldType] = useState<FieldType>("default")
  const [newFieldKey, setNewFieldKey] = useState("")
  const [newFieldValue, setNewFieldValue] = useState("")

  // Check for duplicate keys within the same type
  const isDuplicateKey = useMemo(() => {
    if (!newFieldKey.trim()) return false
    return rows.some(
      r =>
        r.type === newFieldType &&
        r.key.toLowerCase() === newFieldKey.trim().toLowerCase()
    )
  }, [rows, newFieldType, newFieldKey])

  const handleAddField = useCallback(() => {
    const trimmedKey = newFieldKey.trim()
    if (!trimmedKey || isDuplicateKey) return

    const newRow: FieldRow = {
      id: `${newFieldType}-${trimmedKey}-${Date.now()}`,
      type: newFieldType,
      key: trimmedKey,
      value: newFieldValue.trim()
    }

    const updatedRows = [...rows, newRow]
    setRows(updatedRows)
    onChange(rowsToEntry(entry, updatedRows))

    setNewFieldKey("")
    setNewFieldValue("")
  }, [
    newFieldKey,
    newFieldValue,
    newFieldType,
    isDuplicateKey,
    rows,
    entry,
    onChange
  ])

  const handleUpdateRow = useCallback(
    (id: string, field: "type" | "key" | "value", value: string) => {
      const updatedRows = rows.map(row =>
        row.id === id ? { ...row, [field]: value } : row
      )
      setRows(updatedRows)
      onChange(rowsToEntry(entry, updatedRows))
    },
    [rows, entry, onChange]
  )

  const handleDeleteRow = useCallback(
    (id: string) => {
      const updatedRows = rows.filter(row => row.id !== id)
      setRows(updatedRows)
      onChange(rowsToEntry(entry, updatedRows))
    },
    [rows, entry, onChange]
  )

  const inputClassName =
    "block w-full px-3 py-2 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"

  const typeToggleClassName = (isActive: boolean, isRaw: boolean = false) =>
    `px-3 py-2 text-xs font-medium transition-colors ${
      isActive
        ? isRaw
          ? "bg-amber-500 text-white"
          : "bg-primary-600 text-white"
        : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
    }`

  return (
    <div className="border border-neutral-200 rounded-lg bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50 rounded-t-lg">
        <div>
          <h4 className="font-medium text-neutral-900">LO Default Overrides</h4>
          <p className="text-xs text-neutral-500 mt-0.5">
            Configure LO-specific default values for pricing requests
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-200 rounded-md transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Warning Banner */}
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <span className="font-medium">Advanced Feature:</span> Incorrect
            values can cause pricing errors for this loan officer. Only modify
            if you understand how these defaults affect pricing requests.
          </div>
        </div>

        {/* Field Type Legend */}
        <div className="flex gap-4 text-xs text-neutral-500">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-primary-500"></span>
            <span>
              BB Default – Standard request fields (e.g., lock_period)
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            <span>Engine Raw – Engine-specific fields with dot notation</span>
          </div>
        </div>

        {/* Existing Fields */}
        {rows.length > 0 && (
          <div className="space-y-2">
            {rows.map(row => (
              <div
                key={row.id}
                className={`flex gap-2 items-center p-2 rounded-md border ${
                  row.type === "default"
                    ? "border-primary-100 bg-primary-50/30"
                    : "border-amber-100 bg-amber-50/30"
                }`}
              >
                {/* Type Badge (read-only) */}
                <span
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-md shrink-0 ${
                    row.type === "default"
                      ? "bg-primary-100 text-primary-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {row.type === "default" ? "BB" : "Raw"}
                </span>

                {/* Key Input */}
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={row.key}
                    onChange={e =>
                      handleUpdateRow(row.id, "key", e.target.value)
                    }
                    placeholder="Field key"
                    className={inputClassName}
                  />
                </div>

                {/* Value Input */}
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={row.value}
                    onChange={e =>
                      handleUpdateRow(row.id, "value", e.target.value)
                    }
                    placeholder="Value"
                    className={inputClassName}
                  />
                </div>

                {/* Delete Button */}
                <LinkBtnDanger
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleDeleteRow(row.id)
                  }}
                  className="p-2 shrink-0"
                >
                  <Trash2 size={16} />
                </LinkBtnDanger>
              </div>
            ))}
          </div>
        )}

        {rows.length === 0 && (
          <div className="text-neutral-500 text-center py-6 bg-neutral-50 rounded-lg border border-dashed border-neutral-200">
            No default overrides configured for this LO.
          </div>
        )}

        {/* Add New Field */}
        <div className="border-t border-neutral-200 pt-4">
          <h5 className="text-sm font-medium text-neutral-700 mb-3">
            Add New LO Default
          </h5>
          <div className="flex gap-2 items-end">
            {/* Type Toggle */}
            <div className="shrink-0">
              <label className="block text-xs font-medium text-neutral-600 mb-1">
                Type
              </label>
              <div className="flex rounded-md overflow-hidden border border-neutral-200">
                <button
                  type="button"
                  onClick={() => setNewFieldType("default")}
                  className={typeToggleClassName(
                    newFieldType === "default",
                    false
                  )}
                >
                  BB Default
                </button>
                <button
                  type="button"
                  onClick={() => setNewFieldType("raw")}
                  className={typeToggleClassName(newFieldType === "raw", true)}
                >
                  Engine Raw
                </button>
              </div>
            </div>

            {/* Key Input */}
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-neutral-600 mb-1">
                Key
              </label>
              <input
                type="text"
                value={newFieldKey}
                onChange={e => setNewFieldKey(e.target.value)}
                placeholder={
                  newFieldType === "default"
                    ? "e.g. lock_period"
                    : "e.g. criteria.channel"
                }
                className={inputClassName}
              />
            </div>

            {/* Value Input */}
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-neutral-600 mb-1">
                Value
              </label>
              <input
                type="text"
                value={newFieldValue}
                onChange={e => setNewFieldValue(e.target.value)}
                placeholder="e.g. 45"
                className={inputClassName}
              />
            </div>

            {/* Add Button */}
            <button
              type="button"
              onClick={handleAddField}
              disabled={!newFieldKey.trim() || isDuplicateKey}
              className="flex items-center shrink-0 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-neutral-300 disabled:cursor-not-allowed rounded-md transition-colors"
            >
              <Plus size={14} className="mr-1" />
              Add
            </button>
          </div>

          {isDuplicateKey && newFieldKey.trim() && (
            <p className="text-xs text-red-600 mt-2">
              A {newFieldType === "default" ? "BB default" : "raw"} field with
              this key already exists.
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 px-4 py-3 border-t border-neutral-200 bg-neutral-50 rounded-b-lg">
        <LightBtn type="button" onClick={onClose}>
          Done
        </LightBtn>
      </div>
    </div>
  )
}

export default LOEntryEditor
